/**
 * ImpersonationBanner — the standing notice that an admin is acting AS someone.
 *
 * Why it exists: `startImpersonation` swapped the current user and nothing in
 * the app said so — `getImpersonation` had zero consumers, so an admin who
 * clicked "View as this person" was silently left as that person with no way
 * back except signing out. `chrome.tsx` carried the note "(Impersonation banner
 * ports with the identity layer — omitted here.)" since the original port; this
 * is that banner (operator, 2026-08-08).
 *
 * Three jobs, in order of importance:
 *  1. Never let the admin forget. It mounts at the APP ROOT (App.tsx), above
 *     whatever shell the route resolves to — not inside `Screen`, which would
 *     miss the launcher's "no apps are enabled for your role" view, precisely
 *     where an impersonated narrow-role persona lands.
 *  2. One click back to themselves.
 *  3. Close the audit loop: leaving is recorded, and everything done in between
 *     is already attributed to the admin (lib/audit.ts `_actor`, which reads
 *     `getRealUser()` + `getImpersonation()` — never the swapped identity).
 */
import { useEffect, useState } from "react";
import { Btn, Icon, T, alpha } from "@v0y4ge/jrni-ui";
import { useImpersonation, stopImpersonation } from "./access";
import { logAction } from "../lib/audit";
import { toast } from "./toast";
import { useT } from "../lib/i18n";

/** "4 min" / "1 hr 12 min" — how long they have been acting as this person.
 *  i18n: suggested keys `shell.impersonation.justNow` / `.minShort` / `.hrShort`
 *  don't exist in the catalog yet; batched for the next CLAUDE-CODE-REQUESTS
 *  round rather than invented here (the catalog is kit-owned). */
function elapsed(since: number): string {
  const mins = Math.max(0, Math.round((Date.now() - since) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h} hr ${m} min` : `${h} hr`;
}

export function ImpersonationBanner() {
  const t = useT();
  const imp = useImpersonation();
  const [, tick] = useState(0);

  /* Re-render once a minute so the elapsed time stays true without the admin
     touching anything. Only while acting-as — no idle timer otherwise. */
  useEffect(() => {
    if (!imp) return;
    const id = window.setInterval(() => tick((n) => n + 1), 60000);
    return () => window.clearInterval(id);
  }, [imp]);

  if (!imp) return null;

  const who = imp.target.name || imp.target.email || "—";
  const admin = imp.realUser.name || imp.realUser.email || "you";

  const leave = () => {
    /* Recorded BEFORE the swap back, so `_actor()` still resolves the
       acting-as context and the entry reads "<admin> · acting as <who>". */
    logAction("staff", imp.target.id || imp.target.email || who, who, "stopped acting as");
    stopImpersonation();
    toast({ tone: "info", title: t("shell.impersonation.backAs", "You're back as") + " " + admin });
  };

  return (
    <div role="status" aria-live="polite" data-testid="shell-impersonation-banner"
      style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "9px 24px", background: alpha(T.orange1, 12), borderBottom: `1px solid ${alpha(T.orange1, 32)}`, flexShrink: 0 }}>
      <Icon.eye size={14} style={{ color: T.orange1, flexShrink: 0 }} />
      <span style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>
        {t("shell.impersonation.actingAs", "You're acting as")} {who}
      </span>
      <span style={{ fontSize: 12.5, color: T.n2 }}>
        {imp.target.role ? imp.target.role + " · " : ""}
        {t("shell.impersonation.signedInAs", "signed in as")} {admin} · {elapsed(imp.startedAt)}
      </span>
      <span style={{ fontSize: 12, color: T.soft, minWidth: 0 }}>
        {t("shell.impersonation.audited", "Everything you do is recorded against you, on their behalf.")}
      </span>
      <span style={{ display: "flex", gap: 8, marginLeft: "auto", flexShrink: 0 }}>
        <Btn size="sm" variant="secondary" icon={<Icon.x size={12} />} onClick={leave}>
          {t("shell.impersonation.stop", "Stop acting as")} {who.split(" ")[0]}
        </Btn>
      </span>
    </div>
  );
}
